import {Typography, Box, Button, Link, Tooltip} from '@mui/material';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import {Image} from '@jobseeker/lotus/components/Image';
import {useSelector} from 'react-redux';
import type {AppState, Label} from '@jobseeker/common-core/types';
import {
  handleTracking,
  TrackEventUBA,
  getQueryParamsAndSeekerId,
} from '@jobseeker/common-core/utils';
import {formQueryParams, getIdsFromUrl} from '@utils';
import {openUrl} from '@jobseeker/common-core/utils';
import {getRandomColor} from '@jobseeker/lotus/theme';
import React, {useEffect, useState} from 'react';
import {getTalentBridgeCompanyLogos, talentBridgeContants} from '../utils';

export const trimText = (name: string) =>
  name
    ? name
        .trim()
        .toLowerCase()
        .replace(/[`@!&|/\\#,+=()$~%.'"^:;*?<>{}]/g, '')
        .replace(/\s+/g, '-')
        .replace(/--+/g, '-')
    : '';

export const createAdvancedUrl = (
  title: string,
  jobId: number,
  type: string
): string => {
  return `/${type}/${trimText(title)}-${jobId}`;
};

import type {TalentBridgeJobProps} from './TalentBridgeJob.types';

export const TalentBridgeJob = ({job, jobPos}: TalentBridgeJobProps) => {
  const [talentBridgeJobsData, setTalentBridgeJobsData] = useState<string>('');

  useEffect(() => {
    const fetchTalentBridgeData = async () => {
      try {
        const talentBridgeData = await getTalentBridgeCompanyLogos(
          String(job?.id)
        );
        talentBridgeData && setTalentBridgeJobsData(talentBridgeData?.logo);
      } catch (error) {
        console.error('Error fetching TalentBridge job data:', error);
      }
    };
    fetchTalentBridgeData();
  }, []);

  const theme = useWrappedTheme();
  const {userId} = useSelector((state: AppState) => state.auth);
  const refText = formQueryParams(talentBridgeContants.refCode_temp);

  const onClickJob = () => {
    handleTracking('Offline Landing page', {'Listing Click': 'click=1'});

    const {queryParams} = getQueryParamsAndSeekerId();
    const {catId, tagId} = getIdsFromUrl();

    TrackEventUBA('jobClick', 'click', {
      ref: queryParams.ref || '',
      categoryID: catId || '',
      page: 'Talent Bridge Detail Page',
      tagID: tagId || '',
      jobID: job.id || '',
      jobPos: jobPos || '',
      jobRef: queryParams.ref || '',
      jobVersion: '',
      sessionUsername: userId ? userId.toString() : '',
    });

    openUrl(
      `${createAdvancedUrl(
        job.title,
        job.id,
        'j'
      )}?${refText}${`&jobpos=${jobPos}`}`,
      '_blank'
    );
  };

  const randomColor = getRandomColor();

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '70px',
        borderRadius: 2,
        bgcolor: '#ffffff',
        mb: 2.5,
        position: 'relative',
        cursor: 'pointer',
        '&: hover': {
          boxShadow: '0px 5px 15px 0px rgba(0, 0, 0, 0.09)',
        },
        borderLeft: '5px solid #b4b9d8',
        display: 'flex',
      }}
      onClick={onClickJob}
    >
      <Box
        sx={{
          p: '10px 15px 0 ',
          pr: {sm: 0.875, xxs: 0},
          pl: {sm: 2.5, xxs: 1},
          width: {xs: '100%', sm: '75%'},
          display: 'flex',
          verticalAlign: 'top',
        }}
      >
        <Link
          onClick={onClickJob}
          target="_blank"
          href={`${createAdvancedUrl(
            job.title,
            job.id,
            'j'
          )}?${refText}${`&jobpos=${jobPos}`}`}
        >
          <Box
            component="a"
            sx={{
              display: 'inline-block',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '0px 5px 12px 0px rgba(0, 0, 0, 0.19)',
              },
              height: '70px',
              mt: 1.25,
              borderRadius: 2.5,
            }}
          >
            {job.companyData &&
            job.companyData.companyLogoFilePath &&
            job.companyData.companyLogoFilePath != '' &&
            !job.confidential ? (
              <Image
                src={
                  !!talentBridgeJobsData
                    ? talentBridgeJobsData
                    : `${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}default.webp`
                }
                alt={'Logo'}
                width={70}
                height={70}
                style={{
                  borderRadius: '10px',
                  backgroundColor: '#fff',
                  border: '1px solid #ececec',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: 2.5,
                  backgroundColor: randomColor,
                }}
              >
                <Image
                  src={`${process.env.NEXT_PUBLIC_S3_COMPANY_LOGO_BUCKET}default.webp`}
                  alt="Recruiter Logo"
                  width={70}
                  height={70}
                />
              </Box>
            )}
          </Box>
        </Link>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: 1.5,
          }}
        >
          <Box
            sx={{
              ml: '16px',
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xxs: theme.typography.pxToRem(18),
                  sm: theme.typography.pxToRem(14),
                },
                fontWeight: 600,
              }}
            >
              {typeof job?.title === 'string' ? job?.title.split('(')[0] : ''}
            </Typography>
          </Box>
          <Box
            sx={{
              ml: 2,
              mt: 1.25,
              mb: 1.625,
              fontSize: theme.typography.pxToRem(18),
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <Tooltip
              placement="bottom"
              title={job?.location?.map((loc: Label, index: number) => (
                <Typography
                  variant="subtitle2"
                  sx={{
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                    backgroundColor: '#1c2d41',
                    color: '#fff',
                    padding: '1px',
                    borderRadius: 1,
                  }}
                  key={index}
                >
                  ▪ &nbsp;{loc.name}
                </Typography>
              ))}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: '#1c2d41',
                    color: '#fff',
                    p: '16px',
                  },
                },
                arrow: {
                  sx: {
                    color: '#1c2d41',
                  },
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: theme.typography.pxToRem(14),
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontStretch: 'normal',
                  lineHeight: 'normal',
                  letterSpacing: 'normal',
                  textAlign: 'center',
                  color: '#717171',
                }}
              >
                {job.location?.length
                  ? job.location.length > 1
                    ? 'Multiple Locations'
                    : job.location[0].name
                  : ''}
              </Typography>
            </Tooltip>

            <Box
              sx={{
                width: 4,
                height: 4,
                mr: 1.75,
                ml: 1.875,
                borderRadius: '50%',
                backgroundColor: '#cecece',
                display: 'inline-block',
                verticalAlign: 'middle',
              }}
            />
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontStyle: 'normal',
                fontStretch: 'normal',
                lineHeight: 'normal',
                letterSpacing: 'normal',
                textAlign: 'center',
                color: '#717171',
              }}
            >
              {job.min} - {job.max} Years
            </Typography>
          </Box>
          <Box
            sx={{
              ml: 2,
              mb: 3.125,
              display: 'flex',
              flexWrap: 'wrap',
            }}
          >
            {job.tags?.map((tag: Label) => {
              return (
                <Box
                  key={tag.id}
                  component="span"
                  sx={{
                    p: '5px 13px',
                    borderRadius: '13px',
                    bgcolor: '#f4f4f4',
                    mr: 1.25,
                    mb: 1.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: theme.typography.pxToRem(12),
                      fontWeight: 300,
                      fontStyle: 'normal',
                      fontStretch: 'normal',
                      lineHeight: 'normal',
                      letterSpacing: 'normal',
                      textAlign: {xxs: 'center', sm: 'left'},
                      color: '#717171',
                    }}
                  >
                    {tag.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          verticalAlign: 'top',
          textAlign: 'right',
          pl: 0,
          ml: {md: '', xxs: 0},
          display: {xxs: 'none', sm: 'block'},
          pr: {xxs: 1.875, sm: ''},
          width: {xs: '0%', sm: '25%'},
        }}
      >
        <Button
          variant="contained"
          sx={{
            bgcolor: 'primary.main',
            color: '#ffffff',
            p: '13px 28px',
            borderRadius: 2.5,
            fontSize: theme.typography.pxToRem(16),
            fontWeight: 'bold',
            lineHeight: 'normal',
            border: 'unset',
            mt: 5.75,
            mr: 2.5,
            '&:focus': {
              outline: 'unset',
            },
          }}
        >
          Apply Now
        </Button>
      </Box>
    </Box>
  );
};

export default TalentBridgeJob;
